import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiFitToScreenOutline, mdiMagnifyMinusOutline, mdiMagnifyPlusOutline } from '@mdi/js'
import { apiBaseUrl } from '../../api/client'
import { Button } from '../ui/button'
import type { HighlightBox } from '../../lib/documentPageHighlight'

interface DocumentPageHighlightProps {
  org: string
  docUid: string
  page: number
  boxes: HighlightBox[]
  label?: string
  dpi?: number
  /** Called when the reader clicks the page beside the highlight — the
   * "click off it to leave" gesture. Omitted, the stage is inert on click. */
  onDismiss?: () => void
  /** Called when the reader clicks ON the highlight box itself — navigates
   * back to the asset/property or contract/clause the box came from.
   * Omitted (e.g. a cold-opened or pasted link with no `from` origin), a
   * click on the box does nothing and — like today — never dismisses. */
  onHighlightActivate?: () => void
}

/** Screen padding between a box and the frame drawn around it. */
const PAD_PX = 4
/** Stroke of every frame, in screen pixels (see `non-scaling-stroke` below). */
const STROKE_PX = 2
/** Never blow the page image up past this multiple of its rendered pixels. */
const MAX_UPSCALE = 2
/** …but always leave this much room to zoom, even on a huge render. */
const MIN_MAX_ZOOM = 4
/** One-shot pulse duration, ms — must outlast the CSS animation in
 * index.css (3 × 0.9s iterations = 2700 ms) or the rings are unmounted
 * mid-blink. Slower than the original 3 × 0.4s: after the deep link started
 * scrolling the window, the blink had to still be running when the reader's
 * eye arrives, and three fast flashes were over before the smooth scroll
 * settled. */
const PULSE_MS = 2900
/** How long a deep link keeps the box centered while the page finishes
 * loading around it, unless the reader takes the scroll first. */
const FOLLOW_MS = 5000
/** Drift below this is left alone, so the follow never jitters. */
const FOLLOW_TOLERANCE_PX = 24
/** Highest dpi the page-render endpoint accepts (page_render_handler.go). */
const HI_DPI = 300

interface View {
  /** Zoom, 1 = fit-to-page. */
  k: number
  /** Image top-left offset inside the stage, in screen px (≤ 0). */
  tx: number
  ty: number
}

const FIT: View = { k: 1, tx: 0, ty: 0 }

interface Size {
  w: number
  h: number
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(Math.max(n, min), max)
}

/** Keep the stage covered: the image may never expose a gap on any edge. */
function clampView(v: View, size: Size): View {
  return {
    k: v.k,
    tx: clamp(v.tx, size.w * (1 - v.k), 0),
    ty: clamp(v.ty, size.h * (1 - v.k), 0),
  }
}

function unionOf(boxes: HighlightBox[]): HighlightBox | null {
  if (boxes.length === 0) return null
  const x0 = Math.min(...boxes.map(b => b.x))
  const y0 = Math.min(...boxes.map(b => b.y))
  const x1 = Math.max(...boxes.map(b => b.x + b.w))
  const y1 = Math.max(...boxes.map(b => b.y + b.h))
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 }
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Read-only page image + normalized-coordinate SVG overlay — the "here's the
 * proof" viewer for source provenance (spec 2026-07-17-14 §2), turned into a
 * small pan/zoom stage that opens already framed on the highlight
 * (spec 2026-09-05-06).
 *
 * ### Why the view transform is on the `<img>` only
 *
 * The obvious shape — one CSS `scale(k)` on a wrapper holding the image *and*
 * the overlay — silently re-creates the bug this component exists to fix:
 * `vector-effect="non-scaling-stroke"` only neutralizes transforms *inside* the
 * SVG, up to its outermost viewport, so a CSS transform on an ancestor HTML
 * element still scales the rendered stroke (a "2 px" frame draws 16 px at the
 * plan's framing zoom). The same goes for the screen-px padding around a box.
 *
 * So the image carries the transform and the overlay stays pinned at the
 * stage's own size, carrying the view in its *coordinates* instead
 * (`x = tx/stageW + box.x·k`, `w = box.w·k`). `viewBox="0 0 1 1"` +
 * `preserveAspectRatio="none"` is kept, so `stroke-width: 2` is literally 2 CSS
 * px on screen at every zoom, and the inflation is a constant 4 screen px too.
 *
 * The image stays in flow (`block w-full h-auto`), so the stage keeps the
 * page's aspect ratio as its layout height and `k = 1, tx = ty = 0` *is*
 * fit-to-page.
 */
export function DocumentPageHighlight({ org, docUid, page, boxes, label, dpi, onDismiss, onHighlightActivate }: DocumentPageHighlightProps) {
  const { t } = useTranslation('documents')
  const maskId = useId()
  const [imgError, setImgError] = useState(false)
  const stageRef = useRef<HTMLDivElement | null>(null)
  const [size, setSize] = useState<Size>({ w: 0, h: 0 })
  const [natural, setNatural] = useState<Size>({ w: 0, h: 0 })
  const [view, setView] = useState<View>(FIT)
  const [pulsing, setPulsing] = useState(false)
  const [hiRes, setHiRes] = useState(false)
  const framedRef = useRef(false)
  const rafRef = useRef<number | null>(null)
  const viewRef = useRef<View>(FIT)
  const pulseTimerRef = useRef<number | null>(null)
  const scrollRafRef = useRef<number | null>(null)
  const stopFollowRef = useRef<(() => void) | null>(null)
  const probeRef = useRef<HTMLImageElement | null>(null)

  const effectiveDpi = hiRes ? HI_DPI : dpi
  const srcFor = useCallback(
    (d: number | undefined) =>
      `${apiBaseUrl}/api/v1/organizations/${org}/documents/${docUid}/pages/${page}/render${d ? `?dpi=${d}` : ''}`,
    [org, docUid, page],
  )
  const src = srcFor(effectiveDpi)

  const union = useMemo(() => unionOf(boxes), [boxes])

  /* ------------------------------------------------------------- the stage */

  // The overlay's screen size is the only way to express "N screen px" in
  // viewBox units, so it has to be observed rather than assumed.
  useLayoutEffect(() => {
    const el = stageRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const read = () => setSize({ w: el.clientWidth, h: el.clientHeight })
    read()
    const ro = new ResizeObserver(read)
    ro.observe(el)
    return () => ro.disconnect()
  }, [imgError])

  const maxZoom = useMemo(() => {
    if (size.w < 1 || natural.w < 1) return MIN_MAX_ZOOM
    return Math.max(MIN_MAX_ZOOM, (natural.w * MAX_UPSCALE) / size.w)
  }, [size.w, natural.w])

  // Snapshot of the current view for animateTo's start point — refs may not be
  // written during render.
  useEffect(() => {
    viewRef.current = view
  }, [view])

  // Both bounds of the view are expressed against the stage: the translation
  // clamp is `[stageW·(1-k), 0]` and `maxZoom` scales with the stage's width.
  // So a resize (window, sidebar, orientation) can leave an already-valid view
  // out of bounds — a blank strip at the right/bottom edge, or a `k` above the
  // new ceiling. Re-clamp, never re-frame: a resize must not replay the tween
  // or the pulse. Returning the same object when nothing moved lets React bail
  // out, so a valid view is left strictly untouched.
  useEffect(() => {
    setView(v => {
      const next = clampView({ ...v, k: clamp(v.k, 1, maxZoom) }, size)
      return next.k === v.k && next.tx === v.tx && next.ty === v.ty ? v : next
    })
  }, [size, maxZoom])

  const stopAnimation = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [])

  useEffect(() => stopAnimation, [stopAnimation])

  // The pulse timer and the hi-dpi preload both outlive the component
  // otherwise; harmless under React 19 but they would still call setState.
  useEffect(() => () => {
    if (pulseTimerRef.current !== null) window.clearTimeout(pulseTimerRef.current)
    if (scrollRafRef.current !== null) cancelAnimationFrame(scrollRafRef.current)
    stopFollowRef.current?.()
    if (probeRef.current) probeRef.current.onload = null
  }, [])


  /* --------------------------------------- fit-to-page on mount, once */

  useEffect(() => {
    if (framedRef.current) return
    if (!union || size.w < 40 || size.h < 40 || natural.w < 1) return
    framedRef.current = true

    // A deep link opens FIT TO PAGE, always (user, 2026-09-16). The viewer
    // used to open zoomed onto the box — spec 2026-07-17-14's motivating case
    // was a single OCR word on a large-format plan, ~7 px tall at fit, which
    // is unreadable without magnification. On the documents people actually
    // follow these links into (A4 contracts, quotes, statements) that traded
    // the whole page away to enlarge a line that was already legible, and
    // landing inside a wall of glyphs costs more orientation than the zoom
    // buys. The box is marked and blinks; the reader decides whether to zoom,
    // and the manual controls are untouched.
    //
    // Consequence, deliberately accepted: a word-sized box on a big plan now
    // opens small. It is still framed, still pulsing, and one zoom away.
    const opened = FIT

    // Bring the VIEWER itself into the middle of the browser window, not just
    // the highlight into the middle of the viewer. A `?page=&hl=` deep link
    // lands on a document page that is ~2600 px tall, with the viewer well
    // below the fold — so before this, the frame animation and all three
    // pulses played off screen and the reader arrived to a finished, silent
    // highlight after scrolling down to hunt for it.
    //
    // Deferred two frames: arriving here is a navigation, and the router's
    // own scroll restoration runs after this effect — a scroll issued
    // synchronously is undone a frame later (the same trap as the steering
    // queue's mobile detail pane).
    // Center the BOX, not the stage. scrollIntoView({block:'center'})
    // centers the element it is given, and the stage is routinely taller than
    // the window — centering it leaves the highlight low in the viewport
    // (measured: box at 607 px of a 720 px window). The box is where the
    // reader's eye is being sent, so the delta is computed from the box's own
    // rect and applied to the window.
    const recenter = (minDelta: number) => {
      const stage = stageRef.current
      if (!stage) return
      const target = stage.querySelector('[data-testid="source-highlight-box"]') ?? stage
      const rect = target.getBoundingClientRect()
      const delta = rect.top + rect.height / 2 - window.innerHeight / 2
      if (Math.abs(delta) < minDelta) return
      window.scrollBy({ top: delta, behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
    }

    // One scroll is not enough: the page keeps loading ABOVE the viewer
    // after it has framed (the metadata card, the origin's name, the
    // properties), and every late block pushes the box down by its own
    // height. Measured in CI, the box settled 218-282 px below the middle of
    // a 720 px window. So for a short while the page is watched and the box
    // is brought back whenever the layout moves it — until the reader
    // touches the page, from which point the scroll is theirs.
    const followLayout = () => {
      stopFollowRef.current?.()
      const observer = new ResizeObserver(() => recenter(FOLLOW_TOLERANCE_PX))
      observer.observe(document.body)
      if (stageRef.current) observer.observe(stageRef.current)
      const inputs = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const
      const stop = () => {
        observer.disconnect()
        window.clearTimeout(timer)
        inputs.forEach(ev => window.removeEventListener(ev, stop))
        if (stopFollowRef.current === stop) stopFollowRef.current = null
      }
      const timer = window.setTimeout(stop, FOLLOW_MS)
      inputs.forEach(ev => window.addEventListener(ev, stop, { passive: true }))
      stopFollowRef.current = stop
    }

    const centerInWindow = () => {
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = requestAnimationFrame(() => {
          recenter(0)
          followLayout()
        })
      })
    }

    const pulse = () => {
      setPulsing(true)
      pulseTimerRef.current = window.setTimeout(() => setPulsing(false), PULSE_MS)
    }
    if (prefersReducedMotion()) {
      setView(opened)
      centerInWindow()
      return
    }
    // Center AFTER the framing animation, never alongside it: the animation
    // is still moving the box while it runs, so a delta measured up front is
    // stale by the time it is applied — measured, the box landed at 742 px of
    // a 720 px window, i.e. just off the bottom. Framing settles first, then
    // the box's final rect decides the scroll, then it blinks.
    // No zoom to tween any more, so the view is set outright and only the
    // window scroll is animated. The pulse still waits a frame behind it, so
    // the blink starts once the page has stopped moving.
    setView(opened)
    centerInWindow()
    pulse()
  }, [union, size, natural.w, maxZoom, dpi, srcFor])

  // Hi-dpi swap, now driven by the reader's OWN zoom rather than by a framing
  // zoom on open (which no longer exists — a deep link opens fit-to-page).
  // The moment the stage is asked to draw the page larger than the rendition's
  // own pixels, the extra density pays for itself. Preload first, swap after,
  // so the low-res page stays visible throughout.
  useEffect(() => {
    if (hiRes || (dpi ?? 0) >= HI_DPI) return
    if (natural.w < 1 || size.w < 1) return
    if (size.w * view.k <= natural.w) return
    const probe = new Image()
    probe.onload = () => setHiRes(true)
    probe.src = srcFor(HI_DPI)
    probeRef.current = probe
  }, [view.k, hiRes, dpi, natural.w, size.w, srcFor])

  /* ------------------------------------------------------------ zoom & pan */

  const zoomAt = useCallback((px: number, py: number, factor: number) => {
    stopAnimation()
    setView(v => {
      const k = clamp(v.k * factor, 1, maxZoom)
      const f = k / v.k
      return clampView({ k, tx: px - (px - v.tx) * f, ty: py - (py - v.ty) * f }, size)
    })
  }, [maxZoom, size, stopAnimation])

  const zoomByButton = useCallback(
    (factor: number) => zoomAt(size.w / 2, size.h / 2, factor),
    [zoomAt, size],
  )

  const fitPage = useCallback(() => {
    stopAnimation()
    setView(FIT)
  }, [stopAnimation])

  // Non-passive, or the page scrolls under the document instead of zooming.
  useEffect(() => {
    const el = stageRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const r = el.getBoundingClientRect()
      zoomAt(e.clientX - r.left, e.clientY - r.top, Math.exp(-e.deltaY * 0.0015))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [zoomAt])

  const dragRef = useRef<{ x: number; y: number } | null>(null)
  const [dragging, setDragging] = useState(false)
  // Cursor/title affordance only — the box's own click handling below
  // hit-tests independently and does not read this state.
  const [overHighlight, setOverHighlight] = useState(false)
  /** Slop, in screen px, for both the highlight hit-test and the pan-vs-click
   * distinction (see onStageClick below). */
  const CLICK_SLOP_PX = 4

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    stopAnimation()
    dragRef.current = { x: e.clientX, y: e.clientY }
    setDragging(true)
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  // The overlay is `pointer-events-none` (see onStageClick below), so both a
  // click and a hover have to be hit-tested against the rendered box
  // geometry rather than the event target.
  const isPointOnHighlight = useCallback((clientX: number, clientY: number): boolean => {
    const stage = stageRef.current
    if (!stage) return false
    return Array.from(stage.querySelectorAll('[data-testid="source-highlight-box"]')).some(rect => {
      const b = rect.getBoundingClientRect()
      return clientX >= b.left - CLICK_SLOP_PX && clientX <= b.right + CLICK_SLOP_PX
        && clientY >= b.top - CLICK_SLOP_PX && clientY <= b.bottom + CLICK_SLOP_PX
    })
  }, [])

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const from = dragRef.current
    if (!from) {
      if (onHighlightActivate) setOverHighlight(isPointOnHighlight(e.clientX, e.clientY))
      return
    }
    if (overHighlight) setOverHighlight(false)
    const dx = e.clientX - from.x
    const dy = e.clientY - from.y
    dragRef.current = { x: e.clientX, y: e.clientY }
    setView(v => clampView({ ...v, tx: v.tx + dx, ty: v.ty + dy }, size))
  }

  const endDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return
    dragRef.current = null
    setDragging(false)
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
  }

  // Clicking the page BESIDE the highlight leaves the page view — the way
  // clicking off a lightbox closes it. A reader who followed a provenance
  // link is one gesture from where they came from, without hunting for the
  // "Exit page view" button at the top of a page they have scrolled past.
  // Clicking ON the highlight does the opposite: it navigates back to the
  // asset/property or contract/clause the box came from (when the deep link
  // carried one — see onHighlightActivate).
  //
  // Three things must NOT dismiss, or the viewer becomes unusable:
  //  - a pan. Panning ends with a pointerup like any click, so the pointer's
  //    travel since pointerdown is measured and anything past a few px is a
  //    drag, not a click.
  //  - a click on the highlight itself. "Next to" is the whole gesture: the
  //    box is the thing the reader came to look at. It has its own handler
  //    (onHighlightActivate) and must never fall through to onDismiss.
  //  - a click on the zoom / fit controls, which sit inside the stage.
  const downAtRef = useRef<{ x: number; y: number } | null>(null)

  const onStagePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    downAtRef.current = { x: e.clientX, y: e.clientY }
    onPointerDown(e)
  }

  const onStageClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (!onDismiss && !onHighlightActivate) return
    const from = downAtRef.current
    downAtRef.current = null
    if (from && Math.hypot(e.clientX - from.x, e.clientY - from.y) > CLICK_SLOP_PX) return

    const target = e.target as Element | null
    if (target?.closest('[data-testid="document-page-highlight-controls"]')) return

    if (isPointOnHighlight(e.clientX, e.clientY)) {
      // Never dismiss on a box click, whether or not the link carried an
      // origin — a click that lands on the box is never "beside" it.
      onHighlightActivate?.()
      return
    }

    onDismiss?.()
  }

  /* ---------------------------------------------------------------- render */

  // Screen px expressed in the overlay's (unscaled, non-uniform) user units.
  const padX = size.w > 0 ? PAD_PX / size.w : 0
  const padY = size.h > 0 ? PAD_PX / size.h : 0
  const ox = size.w > 0 ? view.tx / size.w : 0
  const oy = size.h > 0 ? view.ty / size.h : 0

  const framedBoxes = boxes.map(b => ({
    box: b,
    x: ox + b.x * view.k - padX,
    y: oy + b.y * view.k - padY,
    w: b.w * view.k + 2 * padX,
    h: b.h * view.k + 2 * padY,
  }))

  const zoomInLabel = t('page_highlight.zoom_in')
  const zoomOutLabel = t('page_highlight.zoom_out')
  const fitLabel = t('page_highlight.fit_page')

  return (
    <div
      className="relative overflow-hidden rounded-lg border border-border bg-card"
      data-testid="document-page-highlight"
    >
      {imgError ? (
        <div className="p-8 text-center text-sm text-muted-foreground" data-testid="document-page-highlight-unavailable">
          {t('page_highlight.render_unavailable')}
        </div>
      ) : (
        <>
          <div
            ref={stageRef}
            className={`relative overflow-hidden touch-none ${
              dragging ? 'cursor-grabbing' : overHighlight && onHighlightActivate ? 'cursor-pointer' : 'cursor-grab'
            }`}
            onPointerDown={onStagePointerDown}
            onClick={onStageClick}
            title={
              overHighlight && onHighlightActivate
                ? t('page_highlight.activate_hint')
                : onDismiss
                  ? t('page_highlight.dismiss_hint')
                  : undefined
            }
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onPointerLeave={() => setOverHighlight(false)}
            data-testid="document-page-highlight-stage"
          >
            <img
              src={src}
              alt={label || t('page_highlight.page_alt', { page })}
              className="block h-auto w-full select-none"
              draggable={false}
              style={{
                transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.k})`,
                transformOrigin: '0 0',
              }}
              data-testid="document-page-highlight-image"
              onLoad={e => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
              onError={() => setImgError(true)}
            />
            {boxes.length > 0 && (
              <svg
                viewBox="0 0 1 1"
                preserveAspectRatio="none"
                className="pointer-events-none absolute inset-0 h-full w-full"
                data-testid="document-page-highlight-overlay"
              >
                <defs>
                  {/* Dim the page and punch the boxes out of the dimmer: the
                      highlighted words keep full contrast instead of being
                      tinted by a fill sitting on top of them. */}
                  <mask id={maskId}>
                    <rect x={0} y={0} width={1} height={1} fill="white" />
                    {framedBoxes.map((f, i) => (
                      <rect key={i} x={f.x} y={f.y} width={f.w} height={f.h} fill="black" />
                    ))}
                  </mask>
                </defs>
                <rect
                  x={0}
                  y={0}
                  width={1}
                  height={1}
                  fill="var(--background)"
                  fillOpacity={0.35}
                  mask={`url(#${maskId})`}
                  data-testid="document-page-highlight-dim"
                />
                {framedBoxes.map((f, i) => (
                  <rect
                    key={i}
                    x={f.x}
                    y={f.y}
                    width={f.w}
                    height={f.h}
                    fill="none"
                    stroke="var(--primary)"
                    strokeWidth={STROKE_PX}
                    vectorEffect="non-scaling-stroke"
                    aria-label={onHighlightActivate ? t('page_highlight.activate_hint') : undefined}
                    data-testid="source-highlight-box"
                    data-box-x={f.box.x}
                    data-box-y={f.box.y}
                    data-box-w={f.box.w}
                    data-box-h={f.box.h}
                  />
                ))}
                {pulsing && framedBoxes.map((f, i) => (
                  <rect
                    key={`pulse-${i}`}
                    className="document-highlight-pulse"
                    x={f.x}
                    y={f.y}
                    width={f.w}
                    height={f.h}
                    fill="none"
                    stroke="var(--primary)"
                    vectorEffect="non-scaling-stroke"
                    data-testid="source-highlight-pulse"
                  />
                ))}
              </svg>
            )}
          </div>
          <div
            className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-background/80 p-1 backdrop-blur-sm"
            data-testid="document-page-highlight-controls"
          >
            <Button
              variant="outline"
              size="icon"
              aria-label={zoomOutLabel}
              title={zoomOutLabel}
              onClick={() => zoomByButton(1 / 1.3)}
              data-testid="document-page-zoom-out"
            >
              <AppIcon path={mdiMagnifyMinusOutline} size="sm" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label={zoomInLabel}
              title={zoomInLabel}
              onClick={() => zoomByButton(1.3)}
              data-testid="document-page-zoom-in"
            >
              <AppIcon path={mdiMagnifyPlusOutline} size="sm" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              aria-label={fitLabel}
              title={fitLabel}
              onClick={fitPage}
              data-testid="document-page-fit"
            >
              <AppIcon path={mdiFitToScreenOutline} size="sm" className="md:mr-1" />
              <span className="hidden md:inline">{fitLabel}</span>
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
